import { z } from 'zod';

import { http, useAPI, objToFormData } from '~~core';

import { RequirementType, finishRequirementsStatusSchema, getRequirementShowResponseSchema, stepsSchema } from '../schemas/requirement';

type BaseSaveImageData = { image: Blob };
type SaveHandwrittenDataLegacy = BaseSaveImageData;
type SaveHandwrittenData = { rubricateKey: string };
type SaveSelfieData = BaseSaveImageData;
type SaveLivenessData = { livenessKey: string };
type SaveBiometricsData = { biometricKey: string };
type SaveDocumentscopyData = { documentscopyKey: string };
type SaveAddressProofData = BaseSaveImageData;
type SaveTokenData = { code: string };
type SaveFacialBiometricsData = { facematchKey: string };
type SavePixData = { pixChargeKey: string };
type SaveInitialsData = { initials: string };
type SaveFederalData = {
  name: string,
  documentation?: string,
  birthday?: string
};
type SaveDigitalCertificateData = {
  certificateKey: string
};

type SaveOfficialDocData = {
  officialDocumentFront: Blob
  officialDocumentBack: Blob
};

type RubricateData = {
  rubricateKey: string;
};

type SaveRequirementData = {
  email: SaveTokenData
  whatsapp: SaveTokenData
  sms: SaveTokenData
  federal_data: SaveFederalData
  official_document : SaveOfficialDocData
  handwritten: SaveHandwrittenData | SaveHandwrittenDataLegacy
  selfie: SaveSelfieData
  liveness: SaveLivenessData
  address_proof: SaveAddressProofData
  pix: SavePixData
  icp_brasil: SaveDigitalCertificateData
  facial_biometrics: SaveFacialBiometricsData
  agree: null
  initials: SaveInitialsData
  manuscript: never
  rubricate: RubricateData,
  biometric: SaveBiometricsData
  documentscopy: SaveDocumentscopyData
};

type SaveRequirementQueryParams = { webotp?: boolean };

type FinishRequirementsData = {
  latitude?: number
  longitude?: number
  origin?: string
};

export async function fetchRequirement<T extends RequirementType>(
  name: T,
  signatureRequestKey: string,
): Promise<z.infer<ReturnType<typeof getRequirementShowResponseSchema<T>>>> {
  const { getEndpoint } = useAPI(signatureRequestKey);

  const responseBody = await http(getEndpoint(`steps/${name}`));
  const schema = getRequirementShowResponseSchema(name);

  return schema.parse(responseBody);
}

// TOGGLE_REUSABLE_MANUSCRIPT_ENABLED
// remove skipFormData and handwritten from formDataSteps
export async function saveRequirement<T extends RequirementType>(name: T, signatureRequestKey: string, data: SaveRequirementData[T], query?: SaveRequirementQueryParams, { skipFormData } = { skipFormData: false }) {
  const { getEndpoint } = useAPI(signatureRequestKey);

  const formDataSteps = ['handwritten', 'official_document', 'selfie', 'address_proof', 'pix'];
  const shouldSendFormData = !skipFormData && formDataSteps.includes(name);
  const bodyData = shouldSendFormData ? objToFormData({ data }) : { data };

  await http(getEndpoint(`steps/${name}/fulfill`), {
    method: 'post',
    body: bodyData,
    query,
  });
}

export async function fetchRequirementSteps(signatureRequestKey: string) {
  const { getEndpoint } = useAPI(signatureRequestKey);

  const data = await http(getEndpoint('steps'));

  return stepsSchema.parse(data).steps;
}

export async function finishRequirements(signatureRequestKey: string, data: FinishRequirementsData) {
  const { getEndpoint } = useAPI(signatureRequestKey);

  const response = await http.raw(getEndpoint('finish'), {
    method: 'POST',
    body: { session: data },
  });

  return response.status;
}

export async function fetchFinishRequirementsStatus(signatureRequestKey: string) {
  const { getEndpoint } = useAPI(signatureRequestKey);

  const data = await http(getEndpoint('status'));

  return finishRequirementsStatusSchema.parse(data);
}
